import React, { useRef, useState } from 'react';
import { IconLayoutNavbarCollapse, IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import OnboardingButton from "./OnboardingButton";
import { cn } from '../utils/cn';
import { useTheme } from '@mui/material/styles';

export const OnboardingButtonDock = ({
  onBack,
  onNext,
  onSave,
  title,
  loading,
  desktopClassName,
  mobileClassName,
  showNextButton = true
}) => {
  const items = [
    { title: 'Back', icon: <IconArrowLeft className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />, onClick: onBack },
    { title: 'Save', icon: title, onClick: onSave },
  ];

  if (showNextButton) {
    items.push({ title: 'Next', icon: <IconArrowRight className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />, onClick: onNext });
  }

  return (
    <>
      <FloatingDockDesktop items={items} className={desktopClassName} />
      <FloatingDockMobile items={items} className={mobileClassName} />
    </>
  );
};

const FloatingDockMobile = ({
  items,
  className
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <div className={cn("relative block md:hidden", className)}>
      <AnimatePresence>
        {open && (
          <motion.div
            layoutId="nav"
            className="absolute bottom-full mb-2 inset-x-0 flex flex-col gap-2">
            {items.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: 10,
                  transition: {
                    delay: idx * 0.05,
                  },
                }}
                transition={{ delay: (items.length - 1 - idx) * 0.05 }}>
                <button
                  onClick={item.onClick}
                  key={item.title}
                  className={`h-10 w-10 rounded-full ${isDarkMode ? 'bg-neutral-800 text-neutral-200' : 'bg-gray-50 text-neutral-800'} flex items-center justify-center`}>
                  <div className="h-4 w-4">{item.icon}</div>
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        className={`h-10 w-10 rounded-full ${isDarkMode ? 'bg-neutral-800 text-neutral-300' : 'bg-gray-50 text-neutral-700'} flex items-center justify-center`}>
        <IconLayoutNavbarCollapse className="h-5 w-5" />
      </button>
    </div>
  );
};

const FloatingDockDesktop = ({
  items,
  className
}) => {
  let mouseX = useMotionValue(Infinity);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <motion.div
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className={cn(
        "mx-auto hidden md:flex h-16 justify-between items-center rounded-3xl px-4 w-[90%] max-w-[500px]",
        isDarkMode ? "bg-neutral-800" : "bg-gray-50",
        className
      )}>
      <div className="flex items-center h-full">
        <IconContainer mouseX={mouseX} {...items[0]} isDarkMode={isDarkMode} />
      </div>
      <div className="flex-grow flex justify-center items-center h-full">
        <IconContainer mouseX={mouseX} {...items[1]} isDarkMode={isDarkMode} />
      </div>
      {items.length > 2 && (
        <div className="flex items-center h-full">
          <IconContainer mouseX={mouseX} {...items[2]} isDarkMode={isDarkMode} />
        </div>
      )}
    </motion.div>
  );
};

function IconContainer({
  mouseX,
  title,
  icon,
  onClick,
  disabled,
  isDarkMode
}) {
  let ref = useRef(null);

  let distance = useTransform(mouseX, (val) => {
    let bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - bounds.x - bounds.width / 2;
  });

  let widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
  let heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);

  let widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
  let heightTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);

  let width = useSpring(widthTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  let height = useSpring(heightTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  let widthIcon = useSpring(widthTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  let heightIcon = useSpring(heightTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  const [hovered, setHovered] = useState(false);

  if (title === 'Save') {
    return (
      <OnboardingButton
        onClick={onClick}
        loading={false}
        disabled={disabled}
        className="w-48 px-6"
      >
        {icon}
      </OnboardingButton>
    );
  }

  return (
    <motion.button
      onClick={onClick}
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      className={`flex items-center justify-center h-full ${isDarkMode ? 'text-neutral-200' : 'text-neutral-800'}`}
    >
      <motion.div
        style={{ width, height }}
        className={`aspect-square rounded-full flex items-center justify-center relative ${
          isDarkMode ? 'bg-neutral-700' : 'bg-gray-200'
        }`}>
        <AnimatePresence>
          {hovered && (
            <motion.div
              initial={{ opacity: 0, y: 10, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: 2, x: "-50%" }}
              className={`px-2 py-0.5 whitespace-pre rounded-md border absolute left-1/2 -translate-x-1/2 -top-8 w-fit text-xs ${
                isDarkMode
                  ? 'bg-neutral-700 border-neutral-600 text-neutral-200'
                  : 'bg-gray-100 border-gray-200 text-neutral-700'
              }`}>
              {title}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div
          style={{ width: widthIcon, height: heightIcon }}
          className="flex items-center justify-center">
          {icon}
        </motion.div>
      </motion.div>
    </motion.button>
  );
}

export default OnboardingButtonDock;
